import { EV_ADOPTION_CLOSEOUT, renderCloseoutBundle } from './closeoutTransportCopy';
import { buildTrendSentence } from './optionalClauseTokens';
import { compareToReference, formatComparatorPhrase, isFiniteNumber } from './narrativeFormatting';

export type EvAdoptionViewId = 'perCapita' | 'projection' | 'map';

export interface EvAdoptionNarrativeInput {
  locationName: string;
  viewId: EvAdoptionViewId;
  observedYear: number;
  pluginCount: number;
  pluginRate: number;
  pluginSharePct: number;
  changeSince2020: number | null;
  californiaPluginRate?: number | null;
  projected2030?: number | null;
  target2030?: number | null;
  projectionMethodLabel?: string;
}

export function buildEvAdoptionAuthoredBundle(input: EvAdoptionNarrativeInput) {
  if (input.viewId === 'map') {
    return {
      overview: '',
      keyInsights: '',
      relevance: '',
      equity: '',
      authoringStatus: 'INHERIT_MAP',
      usedAuthoredCopy: false,
    };
  }

  const block = EV_ADOPTION_CLOSEOUT[input.viewId];
  if (!block) {
    return renderCloseoutBundle(
      EV_ADOPTION_CLOSEOUT.perCapita,
      { locationName: input.locationName },
      { chartId: 'ev_fleet_adoption', viewId: 'perCapita' }
    );
  }

  const trendSentence = buildTrendSentence(input.changeSince2020, 2020);
  const benchmarkSentence =
    input.viewId === 'perCapita' &&
    isFiniteNumber(input.californiaPluginRate) &&
    isFiniteNumber(input.pluginRate)
      ? ` ${formatComparatorPhrase(
          compareToReference(input.pluginRate, input.californiaPluginRate).relation,
          'California'
        )} California’s ${input.californiaPluginRate} plug-in LDVs per 1,000 residents.`
      : '';

  const projectionReferenceSentence =
    input.viewId === 'projection' &&
    input.target2030 != null &&
    input.target2030 > 0
      ? ` An illustrative population-scaled California reference line (not an adopted local target) would be about ${Math.round(input.target2030).toLocaleString()} plug-in LDVs.`
      : '';

  const tokens = {
    locationName: input.locationName,
    latestYear: input.observedYear,
    evRate: input.pluginRate,
    pluginVehicleCount: input.pluginCount,
    pluginShare: Number(input.pluginSharePct.toFixed(1)),
    trendSentence,
    benchmarkSentence,
    projected2030:
      input.projected2030 != null ? Math.round(input.projected2030).toLocaleString() : null,
    projectionMethodLabel: input.projectionMethodLabel ?? 'recent exponential-growth',
    projectionReferenceSentence,
  };

  return renderCloseoutBundle(block, tokens, {
    chartId: 'ev_fleet_adoption',
    viewId: input.viewId,
  });
}

export function evAdoptionOverviewDescription(viewId: EvAdoptionViewId): string {
  if (viewId === 'map') return '';
  return EV_ADOPTION_CLOSEOUT[viewId]?.overview ?? EV_ADOPTION_CLOSEOUT.perCapita.overview;
}

export function buildEvAdoptionKeyInsights(input: EvAdoptionNarrativeInput): string {
  const bundle = buildEvAdoptionAuthoredBundle(input);
  return bundle.keyInsights || `EV adoption data are not available for ${input.locationName}.`;
}
